let variantCounter = 0;

const nextVariantClientId = () => {
  variantCounter += 1;
  return `variant-${Date.now()}-${variantCounter}`;
};

export const calculateDiscountPercent = (actual, selling) => {
  const actualValue = Number(actual);
  const sellingValue = Number(selling);

  if (!actualValue || actualValue <= 0 || !Number.isFinite(sellingValue)) {
    return "";
  }

  return Math.round(((actualValue - sellingValue) / actualValue) * 100).toString();
};

export const calculateSellingPriceFromDiscount = (actual, discount) => {
  const actualValue = Number(actual);
  const discountValue = Number(discount);

  if (!actualValue || actualValue <= 0 || !Number.isFinite(discountValue)) {
    return "";
  }

  return (actualValue - (actualValue * discountValue) / 100).toFixed(2);
};

export const createEmptyVariant = (overrides = {}) => ({
  client_id: nextVariantClientId(),
  variant_id: "",
  variant_name: "",
  title: "",
  sku: "",
  description: "",
  actual_price: "",
  selling_price: "",
  offer_price: "",
  tax: "",
  discount: "",
  stock: "",
  maximum_cart_quantity: "",
  refurbished: 0,
  net_weight: "",
  dimensions: "",
  is_default: 0,
  sort_order: "",
  status: 1,
  image: null,
  existing_image: "",
  imagePreview: "",
  existing_images: [],
  galleryImages: [],
  galleryFiles: [],
  galleryPreviews: [],
  ...overrides,
});

export const hasVariantContent = (variant) => {
  if (!variant) {
    return false;
  }

  return [
    "variant_name",
    "title",
    "sku",
    "description",
    "actual_price",
    "selling_price",
    "offer_price",
    "tax",
    "discount",
    "stock",
    "maximum_cart_quantity",
    "net_weight",
    "dimensions",
  ].some((field) => {
    const value = variant[field];
    return value !== null && value !== undefined && String(value).trim() !== "";
  }) ||
    Boolean(variant.image) ||
    Boolean(variant.existing_image) ||
    (Array.isArray(variant.existing_images) && variant.existing_images.length > 0) ||
    (Array.isArray(variant.galleryFiles) && variant.galleryFiles.length > 0);
};

export const normalizeVariantList = (variants) => {
  if (!Array.isArray(variants) || variants.length === 0) {
    return [];
  }

  const normalized = variants.map((variant, index) =>
    createEmptyVariant({
      ...variant,
      client_id: variant.client_id || nextVariantClientId(),
      variant_id: variant.variant_id ?? "",
      refurbished: Number(variant.refurbished) === 1 ? 1 : 0,
      is_default: Number(variant.is_default) === 1 ? 1 : 0,
      status: Number(variant.status) === 0 ? 0 : 1,
      sort_order:
        variant.sort_order !== null && variant.sort_order !== undefined && String(variant.sort_order).trim() !== ""
          ? String(variant.sort_order)
          : String(index + 1),
    })
  );

  let defaultIndex = normalized.findIndex(
    (variant) => Number(variant.is_default) === 1 && Number(variant.status) === 1
  );

  if (defaultIndex < 0) {
    defaultIndex = normalized.findIndex((variant) => Number(variant.status) === 1);
  }

  if (defaultIndex < 0) {
    defaultIndex = 0;
  }

  return normalized.map((variant, index) => ({
    ...variant,
    is_default: index === defaultIndex ? 1 : 0,
    sort_order:
      variant.sort_order !== null && variant.sort_order !== undefined && String(variant.sort_order).trim() !== ""
        ? String(variant.sort_order)
        : String(index + 1),
  }));
};

export const hydrateVariants = (variants, imageBaseUrl) =>
  normalizeVariantList(
    (variants || []).map((variant, index) =>
      {
        const parsedGalleryImages = (() => {
          if (Array.isArray(variant.images)) {
            return variant.images;
          }

          if (typeof variant.images === "string" && variant.images.trim()) {
            try {
              const parsedImages = JSON.parse(variant.images);
              return Array.isArray(parsedImages) ? parsedImages : [];
            } catch (error) {
              return [];
            }
          }

          return [];
        })();

        return createEmptyVariant({
          ...variant,
          client_id: `existing-${variant.variant_id || index}-${nextVariantClientId()}`,
          variant_id: variant.variant_id ?? "",
          existing_image: variant.image || "",
          imagePreview: variant.image ? `${imageBaseUrl}${variant.image}` : "",
          existing_images: parsedGalleryImages,
          galleryImages: parsedGalleryImages.map((image) => `${imageBaseUrl}${image}`),
          galleryFiles: [],
          galleryPreviews: [],
        });
      }
    )
  );

export const updateVariantDraft = (variant, field, value) => {
  const nextVariant = { ...variant, [field]: value };

  if (field === "actual_price" || field === "selling_price") {
    nextVariant.discount = calculateDiscountPercent(nextVariant.actual_price, nextVariant.selling_price);
  }

  if (field === "discount") {
    const discountNumber = Number(value);
    nextVariant.discount = Number.isFinite(discountNumber) ? Math.round(discountNumber).toString() : "";
    nextVariant.selling_price = calculateSellingPriceFromDiscount(
      nextVariant.actual_price,
      nextVariant.discount
    );
  }

  if (field === "status" && Number(value) === 0) {
    nextVariant.is_default = 0;
  }

  if (field === "is_default" && Number(value) === 1) {
    nextVariant.is_default = 1;
  }

  return nextVariant;
};

export const validateVariants = (variants) => {
  const variantErrors = {};

  (variants || []).forEach((variant) => {
    if (!hasVariantContent(variant)) {
      return;
    }

    const currentErrors = {};

    if (!variant.variant_name.trim()) {
      currentErrors.variant_name = "Variant name is required";
    }

    if (!variant.actual_price || Number(variant.actual_price) <= 0) {
      currentErrors.actual_price = "Actual price must be a positive number";
    }

    if (!variant.selling_price || Number(variant.selling_price) <= 0) {
      currentErrors.selling_price = "Selling price must be a positive number";
    }

    if (variant.tax !== "" && Number.isNaN(Number(variant.tax))) {
      currentErrors.tax = "Tax must be a valid number";
    }

    if (variant.discount !== "" && Number.isNaN(Number(variant.discount))) {
      currentErrors.discount = "Discount must be a valid number";
    }

    if (variant.offer_price !== "" && Number(variant.offer_price) < 0) {
      currentErrors.offer_price = "Offer price must be zero or greater";
    }

    if (variant.stock !== "" && Number(variant.stock) < 0) {
      currentErrors.stock = "Stock must be zero or greater";
    }

    if (
      variant.maximum_cart_quantity !== "" &&
      (!Number.isFinite(Number(variant.maximum_cart_quantity)) || Number(variant.maximum_cart_quantity) <= 0)
    ) {
      currentErrors.maximum_cart_quantity = "Maximum order quantity must be greater than zero";
    }

    if (Object.keys(currentErrors).length > 0) {
      variantErrors[variant.client_id] = currentErrors;
    }
  });

  return variantErrors;
};

export const buildVariantPayload = (variants) =>
  normalizeVariantList((variants || []).filter(hasVariantContent)).map((variant, index) => {
    const uploadKey =
      variant.image instanceof File
        ? `variant_image_${String(variant.client_id).replace(/[^a-zA-Z0-9_-]/g, "_")}`
        : "";
    const galleryUploadKeys = (variant.galleryFiles || []).map(
      (_, index) =>
        `variant_gallery_${String(variant.client_id).replace(/[^a-zA-Z0-9_-]/g, "_")}_${index}`
    );

    return {
      client_id: variant.client_id,
      variant_id: variant.variant_id || "",
      variant_name: variant.variant_name.trim(),
      title: variant.title || "",
      sku: variant.sku || "",
      description: variant.description || "",
      actual_price: variant.actual_price || "",
      selling_price: variant.selling_price || "",
      offer_price: variant.offer_price || "",
      tax: variant.tax || "",
      discount: variant.discount || "",
      stock: variant.stock || "0",
      maximum_cart_quantity: variant.maximum_cart_quantity || "1",
      refurbished: Number(variant.refurbished) === 1 ? 1 : 0,
      net_weight: variant.net_weight || "",
      dimensions: variant.dimensions || "",
      is_default: Number(variant.is_default) === 1 ? 1 : 0,
      sort_order:
        variant.sort_order !== null && variant.sort_order !== undefined && String(variant.sort_order).trim() !== ""
          ? String(variant.sort_order)
          : String(index + 1),
      status: Number(variant.status) === 0 ? 0 : 1,
      existing_image: variant.existing_image || "",
      existing_images: Array.isArray(variant.existing_images) ? variant.existing_images : [],
      image_upload_key: uploadKey,
      gallery_upload_keys: galleryUploadKeys,
    };
  });
